import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

interface CustomSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  options: CustomSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

type MenuPos = {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
};

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<MenuPos | null>(null);

  // El menú se renderiza en un PORTAL a <body> (no como hijo absolute), porque
  // el panel de contenido (`.content-panel-clip` en App) tiene `clip-path`, que
  // recorta cualquier descendiente (incluso `fixed`). Al portalearlo a body
  // queda fuera de ese clip. Como es `fixed`, calculamos su posición desde el
  // rect del botón, y lo recalculamos en scroll/resize.
  const updatePosition = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const spaceBelow = window.innerHeight - r.bottom - margin;
    const spaceAbove = r.top - margin;
    // Si abajo no entra bien y arriba hay más lugar, se abre hacia arriba.
    const openUp = spaceBelow < 220 && spaceAbove > spaceBelow;
    // Tope de alto: ~9 ítems visibles (el resto scrollea). Sin esto, en pantallas
    // altas mostraría las 24 zonas de una y queda larguísimo.
    const MAX_MENU_HEIGHT = 360;
    const avail = openUp ? spaceAbove : spaceBelow;
    setPos({
      left: r.left,
      width: r.width,
      top: openUp ? undefined : r.bottom + gap,
      bottom: openUp ? window.innerHeight - r.top + gap : undefined,
      maxHeight: Math.min(MAX_MENU_HEIGHT, Math.max(160, avail)),
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    // `capture` para agarrar también el scroll del panel de contenido en mobile.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      // No cerrar si el click cae en el botón/wrapper o dentro del menú portaleado.
      if (wrapRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`w-full h-10 flex items-center justify-between px-3 border border-neutral-200 rounded-md text-[15px] bg-surface-card transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-surface-elevated focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary'
        } outline-none`}
      >
        <span className={selected ? 'text-brand-black/90 truncate' : 'text-neutral-400 truncate'}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-neutral-400 shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 overflow-y-auto"
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
            zIndex: 100000,
          }}
        >
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onClick={() => {
                if (!opt.disabled) {
                  onChange(opt.value);
                  setOpen(false);
                }
              }}
              className={`w-full text-left px-3 py-2 text-[15px] flex items-center justify-between transition-colors ${
                opt.disabled
                  ? 'text-neutral-300 cursor-not-allowed'
                  : value === opt.value
                    ? 'bg-neutral-100 text-brand-black/90 font-medium'
                    : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <span className="truncate">{opt.label}</span>
              {value === opt.value && !opt.disabled && (
                <Check className="w-4 h-4 text-brand-primary shrink-0 ml-2" />
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};
